import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  IFileUploadComponent, 
  IFormControlChange,
  IFormValidationErrors
} from '../df-form-preview-interfaces';
import { DfFormErrorMsg } from '../df-form-error-msg';

interface IFileDataObject {
  name?: string;
  fileName?: string;
  type?: string;
  fileType?: string;
  mimeType?: string;
  size?: number;
  fileSize?: number;
  url?: string;
  path?: string;
  data?: string;
}

interface DfFormFileUploadProps {
  id: string;
  properties: IFileUploadComponent;
  validationErrors?: IFormValidationErrors;
  formValue?: File[] | FileList | IFileDataObject[] | string[] | null;
  readonly?: boolean;
  disabled?: boolean;
  touchedFields?: Record<string, boolean>;
  formSubmitted?: boolean;
  mode?: 'edit' | 'preview' | 'test';
  onValueChange?: (change: IFormControlChange) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  className?: string;
  hideLabel?: boolean;
}

interface IFilePreview {
  file: File;
  id: string;
  preview?: string;
  size: number;
  type: string;
  name: string;
}

export const DfFormFileUpload: React.FC<DfFormFileUploadProps> = ({
  id,
  properties,
  validationErrors = {},
  formValue = null,
  readonly = false,
  disabled = false,
  touchedFields = {},
  formSubmitted = false,
  mode = 'test',
  onValueChange,
  onBlur,
  onFocus,
  className = '',
  hideLabel = false
}) => {
  const [files, setFiles] = useState<IFilePreview[]>([]);
  const [isDragOver, setIsDragOver] = useState<boolean>(false);
  const [isTouched, setIsTouched] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Convert FileList or File[] to IFilePreview[]
  const convertToFilePreviews = useCallback((fileList: FileList | File[] | IFileDataObject[] | string[] | null): IFilePreview[] => {
    if (!fileList) return [];
    
    const fileArray = Array.isArray(fileList) ? fileList : Array.from(fileList);
    const results: IFilePreview[] = [];
    
    fileArray.forEach((file, index) => {
      // Check if file is actually a File object
      const isFileObject = (file as any) instanceof File || (file as any) instanceof Blob;
      
      // If it's a string (URL), create a mock File-like object for display
      let preview: string | undefined;
      let fileSize = 0;
      let fileType = '';
      let fileName = '';
      let fileObj: File;
      
      if (isFileObject) {
        // It's a real File/Blob object
        const fileInstance = file as File;
        fileSize = fileInstance.size || 0;
        fileType = fileInstance.type || '';
        fileName = fileInstance.name || `file-${index}`;
        fileObj = fileInstance;
        
        // Create object URL only for image files
        if (fileType.startsWith('image/')) {
          try {
            preview = URL.createObjectURL(fileInstance);
          } catch (error) {
            console.error('Error creating object URL:', error);
            preview = undefined;
          }
        }
      } else if (typeof file === 'string') {
        // It's a string (URL or base64)
        const fileString = file as string;
        fileName = `file-${index}`;
        
        // Extract file type from data URI
        if (fileString.startsWith('data:')) {
          const semicolonIndex = fileString.indexOf(';');
          if (semicolonIndex > 5) {
            fileType = fileString.substring(5, semicolonIndex);
          } else {
            fileType = 'application/octet-stream';
          }
        } else {
          // Try to infer from file extension or default to image/png for URLs
          const extensionMatch = fileString.match(/\.(jpg|jpeg|png|gif|webp|svg|pdf|doc|docx|xls|xlsx|zip|rar)$/i);
          if (extensionMatch) {
            const ext = extensionMatch[1].toLowerCase();
            const mimeTypes: Record<string, string> = {
              jpg: 'image/jpeg',
              jpeg: 'image/jpeg',
              png: 'image/png',
              gif: 'image/gif',
              webp: 'image/webp',
              svg: 'image/svg+xml',
              pdf: 'application/pdf',
              doc: 'application/msword',
              docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
              xls: 'application/vnd.ms-excel',
              xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              zip: 'application/zip',
              rar: 'application/x-rar-compressed'
            };
            fileType = mimeTypes[ext] || 'application/octet-stream';
          } else {
            fileType = 'application/octet-stream';
          }
        }
        
        // Use the string directly as preview if it's an image URL/data URI
        if (fileString.startsWith('data:image') || fileString.startsWith('http://') || fileString.startsWith('https://') || fileString.startsWith('blob:')) {
          preview = fileString;
        }
        
        // Create a minimal File object for the interface
        fileObj = new File([], fileName, { type: fileType });
      } else if (file && typeof file === 'object') {
        // It might be an object with file properties (from API response)
        const fileObject = file as any;
        fileName = fileObject.name || fileObject.fileName || `file-${index}`;
        fileSize = fileObject.size || fileObject.fileSize || 0;
        fileType = fileObject.type || fileObject.fileType || fileObject.mimeType || '';
        
        // Check if there's a URL or path property
        if (fileObject.url || fileObject.path) {
          preview = fileObject.url || fileObject.path;
        } else if (fileType.startsWith('image/') && fileObject.data) {
          // Handle base64 data
          const fileData = fileObject.data;
          preview = typeof fileData === 'string' 
            ? (fileData.startsWith('data:') ? fileData : `data:${fileType};base64,${fileData}`)
            : undefined;
        }
        
        // Create a minimal File object for the interface
        fileObj = new File([], fileName, { type: fileType });
      } else {
        // Unknown format, skip
        return;
      }
      
      results.push({
        file: fileObj,
      id: `${id}-file-${index}`,
        size: fileSize,
        type: fileType,
        name: fileName,
        preview
      });
    });
    
    return results;
  }, [id]);

  // Validate files - disabled for file upload component
  const validateFiles = useCallback((): { isValid: boolean; errors: Record<string, any> } => {
    // Always return valid since we're not maintaining validation
    return { isValid: true, errors: {} };
  }, []);

  // Handle file selection
  const handleFileSelect = useCallback((selectedFiles: FileList | File[]) => {
    if (readonly || disabled) return;

    const newFiles = convertToFilePreviews(selectedFiles);
    // If files already exist, always append new files (for "add more" functionality)
    // If no files exist, replace (regardless of multiple setting)
    const shouldAppend = files.length > 0;
    const allFiles = shouldAppend ? [...files, ...newFiles] : newFiles;
    
    // Apply max files limit
    const maxFiles = properties?.validation?.maxFiles || Infinity;
    const limitedFiles = allFiles.slice(0, maxFiles);
    
    setFiles(limitedFiles);
    setIsTouched(true);
    
    if (onValueChange) {
      const validation = validateFiles();
      const change: IFormControlChange = {
        id,
        value: limitedFiles.map(f => f.file),
        isValid: validation.isValid,
        errors: validation.errors,
      };
      onValueChange(change);
    }
  }, [files, properties, readonly, disabled, onValueChange, validateFiles, convertToFilePreviews, id]);

  // Handle drag events
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!readonly && !disabled) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    if (readonly || disabled) return;
    
    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length > 0) {
      handleFileSelect(droppedFiles);
    }
  };

  // Handle file input change
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      handleFileSelect(selectedFiles);
      // Reset input value to allow selecting the same file again
      e.target.value = '';
    }
  };

  // Handle focus
  const handleFocus = () => {
    if (onFocus) {
      onFocus();
    }
  };

  // Handle blur
  const handleBlur = () => {
    setIsTouched(true);
    
    if (mode === 'test' && onValueChange) {
      const validation = validateFiles();
      const change: IFormControlChange = {
        id,
        value: files.map(f => f.file),
        isValid: validation.isValid,
        errors: validation.errors,
      };
      onValueChange(change);
    }
    
    if (onBlur) {
      onBlur();
    }
  };

  // Remove file
  const removeFile = (fileId: string) => {
    if (readonly || disabled) return;
    
    const updatedFiles = files.filter(f => f.id !== fileId);
    setFiles(updatedFiles);
    setIsTouched(true);
    
    if (onValueChange) {
      const validation = validateFiles();
      const change: IFormControlChange = {
        id,
        value: updatedFiles.map(f => f.file),
        isValid: validation.isValid,
        errors: validation.errors,
      };
      onValueChange(change);
    }
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Get file icon based on type
  const getFileIcon = (fileType: string): string => {
    if (fileType.startsWith('image/')) return '🖼️';
    if (fileType.startsWith('video/')) return '🎥';
    if (fileType.startsWith('audio/')) return '🎵';
    if (fileType.includes('pdf')) return '📄';
    if (fileType.includes('word') || fileType.includes('document')) return '📝';
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return '📊';
    if (fileType.includes('zip') || fileType.includes('rar')) return '📦';
    return '📁';
  };

  // Update touched fields
  useEffect(() => {
    if (isTouched) {
      touchedFields[id] = true;
    }
  }, [isTouched, id, touchedFields]);

  // Reset touched state when switching modes, but preserve files
  useEffect(() => {
    if (mode === 'edit') {
      setIsTouched(false);
      // Don't clear files - let formValue prop handle the state
    } else if (mode === 'test') {
      setIsTouched(false);
      // Don't clear files - let formValue prop handle the state
    }
  }, [mode]);

  // Update value when formValue prop changes or on mount
  useEffect(() => {
    // Always convert formValue, even if it's null/undefined (will return empty array)
    const newFiles = convertToFilePreviews(formValue);
    setFiles(newFiles);
  }, [formValue, convertToFilePreviews]);

  // Mark as touched when form is submitted
  useEffect(() => {
    if (formSubmitted) {
      setIsTouched(true);
    }
  }, [formSubmitted]);

  // Clean up object URLs (only for blob URLs, not data URIs or http URLs)
  useEffect(() => {
    return () => {
      files.forEach(file => {
        if (file.preview && file.preview.startsWith('blob:')) {
          try {
          URL.revokeObjectURL(file.preview);
          } catch (error) {
            console.error('Error revoking object URL:', error);
          }
        }
      });
    };
  }, [files]);

  // Get label alignment class
  const getLabelAlignmentClass = (): string => {
    const alignment = properties?.styles?.labelAlignment;
    return alignment === 'left' ? 'label-left' : 'label-top';
  };

  // Get upload area classes
  const getUploadAreaClasses = (): string => {
    const base = 'file-upload-area';
    const dragOverClass = isDragOver ? 'drag-over' : '';
    const readonlyClass = readonly ? 'readonly' : '';
    const disabledClass = disabled ? 'disabled' : '';
    
    // No validation logic - always return base classes
    return `${base} ${dragOverClass} ${readonlyClass} ${disabledClass} ${className}`.trim();
  };

  const shouldDisable = disabled || readonly || properties?.validation?.readonly;
  const isMultiple = properties?.validation?.multiple || false;
  const acceptTypes = properties?.validation?.accept || '*/*';
  const maxFiles = properties?.validation?.maxFiles || Infinity;
  const canAddMoreFiles = files.length < maxFiles && (isMultiple || maxFiles > 1);
  const isEditMode = mode === 'edit';
  const isPreviewOrTestMode = mode === 'preview' || mode === 'test';

  // Filter image files for preview/test mode display
  const imageFiles = files.filter(file => file.type.startsWith('image/'));
  const nonImageFiles = files.filter(file => !file.type.startsWith('image/'));
  const hasImages = imageFiles.length > 0;
  const shouldShowImageDisplay = isPreviewOrTestMode && hasImages;

  // Handle add more files button click
  const handleAddMoreFiles = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!shouldDisable && fileInputRef.current && isEditMode) {
      // Reset input value to allow selecting the same file again
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  // Render image display for preview/test mode
  if (shouldShowImageDisplay) {
    return (
      <div className={`form-group ${getLabelAlignmentClass()}`}>
        {!hideLabel && (
          <label htmlFor={id} className="form-label">
            {properties.basic?.label || 'Attachments'}
            {properties.validation?.required && (
              <span className="required-indicator">*</span>
            )}
          </label>
        )}

        <div className="file-upload-wrapper">
          <div className="file-image-display">
            {imageFiles.map((file) => (
              <div key={file.id} className="file-image-container">
                {file.preview && (
                  <img 
                    src={file.preview} 
                    alt={file.name} 
                    className="file-image-preview"
                  />
                )}
                <div className="file-image-info">
                  <div className="file-name" title={file.name}>{file.name}</div>
                  <div className="file-size">{formatFileSize(file.size)}</div>
                </div>
              </div>
            ))}
            {nonImageFiles.length > 0 && (
              <div className="file-other-files">
                <div className="file-other-files-label">Other Files:</div>
                {nonImageFiles.map((file) => (
                  <div key={file.id} className="file-item-minimal">
                    <div className="file-icon">{getFileIcon(file.type)}</div>
                    <div className="file-info">
                      <div className="file-name" title={file.name}>{file.name}</div>
                      <div className="file-size">{formatFileSize(file.size)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DfFormErrorMsg
            validationErrors={validationErrors}
            fieldId={id}
            touchedFields={touchedFields}
            formSubmitted={formSubmitted}
            properties={properties}
            localValidation={validateFiles()}
            isTouched={isTouched}
            mode={mode}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={`form-group ${getLabelAlignmentClass()}`}>
      {!hideLabel && (
        <label htmlFor={id} className="form-label">
          {properties.basic?.label || 'Attachments'}
          {properties.validation?.required && (
            <span className="required-indicator">*</span>
          )}
        </label>
      )}

      <div className="file-upload-wrapper">
        <div
          className={getUploadAreaClasses()}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={(e) => {
            // Only trigger file picker in edit mode when no files exist and not clicking on file items
            const target = e.target as HTMLElement;
            const isClickOnFileItem = target.closest('.file-item') !== null;
            const isClickOnRemoveButton = target.closest('.file-remove') !== null;
            
            if (isEditMode && !shouldDisable && files.length === 0 && !isClickOnFileItem && !isClickOnRemoveButton) {
              fileInputRef.current?.click();
            }
          }}
          onFocus={handleFocus}
          onBlur={handleBlur}
          tabIndex={shouldDisable ? -1 : 0}
        >
          <input
            ref={fileInputRef}
            type="file"
            id={id}
            multiple={isMultiple}
            accept={acceptTypes}
            onChange={handleFileInputChange}
            disabled={shouldDisable}
            style={{ display: 'none' }}
          />
          
          {files.length === 0 ? (
            <div 
              className="upload-placeholder"
              onClick={(e) => {
                e.stopPropagation();
                if (isEditMode && !shouldDisable) {
                  fileInputRef.current?.click();
                }
              }}
            >
              <div className="upload-icon">📁</div>
              <div className="upload-text">
                {/* <p>Drop files here or click to browse</p> */}
                {properties.basic.placeholder && (
                  <p className="upload-hint">{properties.basic.placeholder}</p>
                )}
              </div>
            </div>
          ) : (
            <div className="file-list">
              {files.map((file) => (
                <div key={file.id} className="file-item">
                  <div className="file-preview">
                    {file.preview ? (
                      <img src={file.preview} alt={file.name} className="file-image" />
                    ) : (
                      <div className="file-icon">{getFileIcon(file.type)}</div>
                    )}
                  </div>
                  <div className="file-info">
                    <div className="file-name" title={file.name}>{file.name}</div>
                    <div className="file-size">{formatFileSize(file.size)}</div>
                  </div>
                  {isEditMode && !shouldDisable && (
                    <button
                      type="button"
                      className="file-remove"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile(file.id);
                      }}
                      aria-label={`Remove ${file.name}`}
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {files.length > 0 && canAddMoreFiles && !shouldDisable && isEditMode && (
          <button
            type="button"
            className="file-add-more"
            onClick={handleAddMoreFiles}
            aria-label="Add more files"
          >
            <svg
              className="file-add-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 5v14M5 12h14" />
            </svg>
            <span className="file-add-text">Add More Files</span>
          </button>
        )}

        <DfFormErrorMsg
          validationErrors={validationErrors}
          fieldId={id}
          touchedFields={touchedFields}
          formSubmitted={formSubmitted}
          properties={properties}
          localValidation={validateFiles()}
          isTouched={isTouched}
          mode={mode}
        />
      </div>
    </div>
  );
};

export default DfFormFileUpload;
